// Brief 103 — Bug 2 / BM-A0 hotfix. AI-generated CCL section bodies are plain
// Markdown. When inserting drafted text into the TipTap editor we must convert
// that Markdown to HTML first; inserting raw Markdown leaves literal markup
// (e.g. "## 5. OUR FEES AND COSTS") visible in a client-facing legal document.
//
// This is the render path for accepted AI drafts (CclEditor.handleInsertDraft).
// It is a small, block-aware Markdown→HTML converter for the subset the CCL
// drafter emits: ATX headings, unordered/ordered lists, bold, italics and
// paragraphs. Two properties matter for the reported defects:
//
//   1. ATX headings ("## Heading") become <h2>/<h3> rather than literal text.
//      Levels are clamped to h2/h3 because the editor's StarterKit only enables
//      heading levels 2 and 3 — an <h1>/<h4> node would be dropped on insert.
//   2. Consecutive list items stay in a SINGLE <ul>/<ol> even when the model
//      separates them with blank lines. Previously each blank-line-separated
//      bullet became its own one-item list with a paragraph gap between, which
//      double-spaced the list. A blank line no longer breaks an open list.
//
// All input is HTML-escaped first, so model-emitted tags render as inert text
// and no script can survive — this function only ever emits its own known-safe
// tag set (sanitisation by construction).

function escapeHtml(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function inlineMarkup(s) {
  return s
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
}

export function markdownToHtml(text) {
  if (!text) return "";

  const lines = escapeHtml(text).replace(/\r\n?/g, "\n").split("\n");

  const out = [];
  let listType = null; // "ul" | "ol" | null
  let para = [];

  const flushPara = () => {
    if (para.length) {
      out.push(`<p>${inlineMarkup(para.join("<br/>"))}</p>`);
      para = [];
    }
  };
  const closeList = () => {
    if (listType) {
      out.push(`</${listType}>`);
      listType = null;
    }
  };
  const openList = (type) => {
    if (listType !== type) {
      closeList();
      out.push(`<${type}>`);
      listType = type;
    }
  };

  for (const raw of lines) {
    const line = raw.trim();

    // Blank line: end the current paragraph but DO NOT close an open list, so
    // bullets the model separated with blank lines stay in one list.
    if (!line) {
      flushPara();
      continue;
    }

    let m;
    if ((m = line.match(/^(#{1,6})\s+(.+)$/))) {
      flushPara();
      closeList();
      const level = m[1].length <= 2 ? 2 : 3;
      out.push(`<h${level}>${inlineMarkup(m[2])}</h${level}>`);
    } else if ((m = line.match(/^[-*]\s+(.+)$/))) {
      flushPara();
      openList("ul");
      out.push(`<li>${inlineMarkup(m[1])}</li>`);
    } else if ((m = line.match(/^\d+\.\s+(.+)$/))) {
      flushPara();
      openList("ol");
      out.push(`<li>${inlineMarkup(m[1])}</li>`);
    } else {
      // Plain prose closes any open list and buffers into a paragraph.
      closeList();
      para.push(line);
    }
  }

  flushPara();
  closeList();
  return out.join("");
}
